'use client';

import React from 'react';
import Icon from '@/components/ui/AppIcon';

// Backend integration point: auto-calculated from submitted form data via /api/mel/calculate
const melEstimates = {
  khapat: {
    waterConsumed: '—',
    foodWaste: '—',
    electricalUse: '—',
    note: 'MEL Khapat values are calculated from your habit adoption levels after submission.',
  },
  bachat: {
    energySaved: 'Est. ₹4,200',
    transportSaved: 'Est. 840 km',
    waterSaved: 'Est. 18,000 L',
    sakashTime: 'Est. 280 hrs',
  },
  kamai: {
    netSavings: 'Est. ₹6,100',
    treesSequestering: 'Reported trees × CO₂ rate',
    communityImpact: 'Based on awareness reach',
  },
};

export default function MELAutoSummary() {
  return (
    <div className="bg-card rounded-2xl border border-border shadow-card p-5">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="ChartBarIcon" size={18} variant="outline" className="text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-foreground">MEL Framework Preview</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Monitoring, Evaluation &amp; Learning — estimated values based on your entries. Final calculations are generated after submission.
          </p>
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-muted text-muted-foreground text-[10px] font-semibold flex-shrink-0">
          <Icon name="ClockIcon" size={10} variant="outline" />
          Auto-calculated
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Khapat */}
        <div className="rounded-xl border border-[var(--danger)]/20 bg-[var(--danger-bg)]/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-[var(--danger-bg)] flex items-center justify-center">
              <Icon name="ChartBarIcon" size={14} variant="outline" className="text-[var(--danger)]" />
            </div>
            <div>
              <p className="text-xs font-extrabold text-[var(--danger)]">Khapat</p>
              <p className="text-[10px] text-muted-foreground">Consumption</p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Water', value: melEstimates?.khapat?.waterConsumed },
              { label: 'Food Waste', value: melEstimates?.khapat?.foodWaste },
              { label: 'Electrical', value: melEstimates?.khapat?.electricalUse },
            ]?.map((item) => (
              <div key={`mel-k-${item?.label}`} className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">{item?.label}</span>
                <span className="text-xs font-bold text-foreground tabular-nums">{item?.value}</span>
              </div>
            ))}
            <p className="text-[10px] text-muted-foreground mt-2 leading-relaxed">{melEstimates?.khapat?.note}</p>
          </div>
        </div>

        {/* Bachat */}
        <div className="rounded-xl border border-[var(--info)]/20 bg-[var(--info-bg)]/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-[var(--info-bg)] flex items-center justify-center">
              <Icon name="ArrowTrendingDownIcon" size={14} variant="outline" className="text-[var(--info)]" />
            </div>
            <div>
              <p className="text-xs font-extrabold text-[var(--info)]">Bachat</p>
              <p className="text-[10px] text-muted-foreground">Savings</p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Energy Saved', value: melEstimates?.bachat?.energySaved },
              { label: 'Transport', value: melEstimates?.bachat?.transportSaved },
              { label: 'Water Saved', value: melEstimates?.bachat?.waterSaved },
              { label: 'Sakash Time', value: melEstimates?.bachat?.sakashTime },
            ]?.map((item) => (
              <div key={`mel-b-${item?.label}`} className="flex justify-between items-center">
                <span className="text-xs text-muted-foreground">{item?.label}</span>
                <span className="text-xs font-bold text-foreground tabular-nums">{item?.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Kamai */}
        <div className="rounded-xl border border-[var(--success)]/20 bg-[var(--success-bg)]/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-[var(--success-bg)] flex items-center justify-center">
              <Icon name="SparklesIcon" size={14} variant="outline" className="text-[var(--success)]" />
            </div>
            <div>
              <p className="text-xs font-extrabold text-[var(--success)]">Kamai</p>
              <p className="text-[10px] text-muted-foreground">Value Earned</p>
            </div>
          </div>
          <div className="space-y-2">
            {[
              { label: 'Net Savings', value: melEstimates?.kamai?.netSavings },
              { label: 'Trees Impact', value: melEstimates?.kamai?.treesSequestering },
              { label: 'Community', value: melEstimates?.kamai?.communityImpact },
            ]?.map((item) => (
              <div key={`mel-ka-${item?.label}`} className="flex justify-between items-center gap-2">
                <span className="text-xs text-muted-foreground flex-shrink-0">{item?.label}</span>
                <span className="text-xs font-bold text-foreground tabular-nums text-right">{item?.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2 border-t border-[var(--success)]/20">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Final Kamai values are calculated based on your complete submission including habit adoption scores and activity counts.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}